import { combineReducers, configureStore } from "@reduxjs/toolkit";
import authorisationReducer from "../store/slices/authorisation";
import residentsReducer from "../store/slices/residents";
import overlayReducer from "../store/slices/overlay";
import networkReducer from "../store/slices/network";
import formEntriesReducer from "../store/slices/formEntries";
import medicationsReducer from "../store/slices/medications";
import authModalReducer from "../store/slices/authModal";
import { authorisationApi } from "./api/authorisationApi";
import { categoriesApi } from "./api/categoriesApi";
import { countriesApi } from "./api/countriesApi";
import { locationsApi } from "./api/locationsApi";
import { residentsApi } from "./api/residentsApi";
import { rolesApi } from "./api/rolesApi";
import { usersApi } from "./api/usersApi";
import { medicationsApi } from "./api/medicationsApi";
import { shiftHandoverApi } from "./api/shiftHandoverApi";
import { auditLogsApi } from "./api/auditLogsApi";
import { reportsApi } from "./api/reportsApi";
import createIndexedDBStorage from "redux-persist-indexeddb-storage";
import { adminAssessmentsApi } from "./api/adminAssessmentsApi";

import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import { setupListeners } from "@reduxjs/toolkit/query";
import { formsApi } from "./api/formsApi";
import { staticFormsApi } from "./api/staticFormsApi";
import { calendarApi } from "./api/calendarApi";
import failedRequestsReducer from "../store/slices/failedRequests";
import { offlineApi } from "./api/offlineApi";
import formsReducer from "../store/slices/forms";
import { statusFlagsApi } from "./api/statusFlagsApi";
import fluidIntakeReducer from "../store/slices/fluidIntake";
import bowelMovementsReducer from "../store/slices/bowelMovements";
import fluidOutputReducer from "../store/slices/fluidOutput";
import prefetchMetadataReducer from "../store/slices/prefetchMetadata";
import { bodyMapApi } from "./api/bodyMapApi";
import urgentConcernsReducer from "../store/slices/urgentConcerns";
import { urgentConcernsApi } from "./api/urgentConcernsApi";

const persistedStorage = createIndexedDBStorage("icare-db");

import {
  DISABLE_OFFLINE_PERSISTENCE,
  DISABLE_OFFLINE_RETRIEVAL,
} from "../config";

const storage = {
  getItem: async (key: string) => {
    if (DISABLE_OFFLINE_RETRIEVAL) return null;
    return persistedStorage.getItem(key);
  },
  setItem: async (key: string, value: any) => {
    if (DISABLE_OFFLINE_PERSISTENCE) return;
    return persistedStorage.setItem(key, value);
  },
  removeItem: async (key: string) => {
    if (DISABLE_OFFLINE_PERSISTENCE) return;
    return persistedStorage.removeItem(key);
  },
};

const reducers = combineReducers({
  authorisation: authorisationReducer,
  residents: residentsReducer,
  overlay: overlayReducer,
  network: networkReducer,
  formEntries: formEntriesReducer,
  medications: medicationsReducer,
  authModal: authModalReducer,
  failedRequests: failedRequestsReducer,
  forms: formsReducer,
  fluidIntake: fluidIntakeReducer,
  bowelMovements: bowelMovementsReducer,
  fluidOutput: fluidOutputReducer,
  prefetchMetadata: prefetchMetadataReducer,
  urgentConcerns: urgentConcernsReducer,
  [auditLogsApi.reducerPath]: auditLogsApi.reducer,
  [authorisationApi.reducerPath]: authorisationApi.reducer,
  [adminAssessmentsApi.reducerPath]: adminAssessmentsApi.reducer,
  [categoriesApi.reducerPath]: categoriesApi.reducer,
  [countriesApi.reducerPath]: countriesApi.reducer,
  [locationsApi.reducerPath]: locationsApi.reducer,
  [residentsApi.reducerPath]: residentsApi.reducer,
  [rolesApi.reducerPath]: rolesApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [medicationsApi.reducerPath]: medicationsApi.reducer,
  [formsApi.reducerPath]: formsApi.reducer,
  [staticFormsApi.reducerPath]: staticFormsApi.reducer,
  [calendarApi.reducerPath]: calendarApi.reducer,
  [shiftHandoverApi.reducerPath]: shiftHandoverApi.reducer,
  [offlineApi.reducerPath]: offlineApi.reducer,
  [statusFlagsApi.reducerPath]: statusFlagsApi.reducer,
  [reportsApi.reducerPath]: reportsApi.reducer,
  [bodyMapApi.reducerPath]: bodyMapApi.reducer,
  [urgentConcernsApi.reducerPath]: urgentConcernsApi.reducer,
});

// const encryptTransform =
//   require("redux-persist-transform-encrypt/lib").encryptTransform;

// const encryptor = encryptTransform({
//   secretKey: "my-super-secret-key", // Replace with a secure key
//   onError: (error) => {
//     console.error("Encryption error:", error);
//   },
// });

const persistConfig = {
  key: "root",
  version: 2,
  storage,
  whitelist: [
    "formEntries",
    "residents",
    "medications",
    "failedRequests",
    "forms",
    "fluidIntake",
    "bowelMovements",
    "fluidOutput",
    "prefetchMetadata",
    countriesApi.reducerPath,
    locationsApi.reducerPath,
    offlineApi.reducerPath,
  ],
  blacklist: [residentsApi.reducerPath],
  // transforms: [encryptor], // Add the encryption transform here
};

const persistedReducer = persistReducer(persistConfig, reducers);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    const middleware = getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      auditLogsApi.middleware,
      authorisationApi.middleware,
      adminAssessmentsApi.middleware,
      categoriesApi.middleware,
      countriesApi.middleware,
      residentsApi.middleware,
      usersApi.middleware,
      rolesApi.middleware,
      locationsApi.middleware,
      medicationsApi.middleware,
      formsApi.middleware,
      staticFormsApi.middleware,
      calendarApi.middleware,
      shiftHandoverApi.middleware,
      offlineApi.middleware,
      statusFlagsApi.middleware,
      reportsApi.middleware,
      bodyMapApi.middleware,
      urgentConcernsApi.middleware,
    );

    return middleware as any;
  },
});

setupListeners(store.dispatch);

export const persistor = persistStore(store, null, () => {
  console.log("Persistance completed");
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
